import {useEffect, useState} from 'react'
import {ALLOWED_IMAGE_EXT, ALLOWED_IMAGE_SIZE} from './constants'
import {getCountries, getCountryCallingCode} from 'libphonenumber-js'
import countries from 'i18n-iso-countries'
import metadata from 'libphonenumber-js/metadata.min.json'
import {parsePhoneNumber} from 'libphonenumber-js/core'
import redQuestion from 'assets/img/red-question-mark.svg'

export const bytesToMegaBytes = (bytes) => {
  return (bytes / (1024 * 1024)).toFixed(1)
}

export const verifyImageSize = (file) => {
  const fileSize = bytesToMegaBytes(file.size)
  if (fileSize > ALLOWED_IMAGE_SIZE) return false
  return true
}

export const verifyImageType = (file) => {
  const imageType = file.type.split('/')[1]
  if (ALLOWED_IMAGE_EXT[imageType] === undefined) return false
  return true
}

export function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export const getFlagEmoji = (countryCode) => {
  return String?.fromCodePoint(
    ...countryCode
      ?.toUpperCase()
      ?.split('')
      ?.map((char) => 127397 + char?.charCodeAt())
  )
}

// Get a list of all countries with their calling codes
export const countryList = getCountries()?.map((countryCode) => {
  const callingCode = getCountryCallingCode(countryCode, metadata)
  return {
    code: countryCode,
    name:
      countryCode === 'AC'
        ? 'Ascension Island'
        : countryCode === 'TA'
          ? 'Tristan da Cunha'
          : countries?.getName(countryCode, 'en'),
    callingCode: `+${callingCode}`,
    flag: getFlagEmoji(countryCode),
  }
})

export const getCountryFromNumber = (phoneNumber) => {
  try {
    const parsedNumber = parsePhoneNumber(phoneNumber, metadata)
    if (parsedNumber) {
      const callingCode = parsedNumber?.countryCallingCode
      return callingCode
    }
  } catch (error) {
    return null
  }
}

export const getMobileNumberWithoutCode = (phoneNumber) => {
  try {
    const parsedNumber = parsePhoneNumber(phoneNumber, metadata)
    if (parsedNumber) {
      const mobile = parsedNumber?.nationalNumber // Extract the number without country code
      return mobile
    }
  } catch (error) {
    return null
  }
}

export const ErrorLine = ({message}) => (
  <div className="flex gap-x-2 items-center text-warning-red text-sm mt-2">
    <img src={redQuestion} alt="!" />
    <span>{message}</span>
  </div>
)

export async function fetchFxRate(base, target) {
  // 1) Fetch the full rates object for base currency
  const res = await fetch(
    `https://open.er-api.com/v6/latest/${encodeURIComponent(base)}`
  )
  const json = await res.json()

  // 2) Check for success
  if (json.result !== 'success') {
    throw new Error(
      `Currency Conversion lookup failed: ${json['error-type'] || 'unknown error'}`
    )
  }

  // 3) Pull out the target rate
  const rate = json.rates?.[target]
  if (rate == null) {
    throw new Error(`No ${target} rate available for base ${base}`)
  }

  return rate
}

export const formatPrice = (price) => {
  return (price * 1).toLocaleString('us-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function formatTime(seconds) {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  return `${hrs}h ${mins}m ${secs}s`
}

export const cleanBolFileName = (filename) => {
  const name = filename?.split('/')?.pop() || filename

  // Try multiple patterns that might be used
  let cleanedName = name

  // Remove BOL_[numbers]_ prefix
  cleanedName = cleanedName?.replace(/^BOL_\d+_/, '')

  // Remove bol_cartid_ prefix (in case it's lowercase)
  cleanedName = cleanedName?.replace(/^bol_cartid_/, '')

  // Remove any BOL_ prefix followed by anything up to underscore
  cleanedName = cleanedName?.replace(/^BOL_[^_]+_/, '')

  return cleanedName || name
}

export const cleanAttachmentFileName = (filename) => {
  const name = filename?.split('/')?.pop() || filename

  // Try multiple patterns that might be used
  let cleanedName = name

  // Remove BOL_[numbers]_ prefix
  cleanedName = cleanedName?.replace(/^DOC_\d+_/, '')

  // Remove bol_cartid_ prefix (in case it's lowercase)
  cleanedName = cleanedName?.replace(/^DOC_cartid_/, '')

  // Remove any BOL_ prefix followed by anything up to underscore
  cleanedName = cleanedName?.replace(/^DOC_[^_]+_/, '')

  return cleanedName || name
}

// Calculate distance between two points using Haversine formula
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371 // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * (Math.PI / 180)
  const dLon = (lon2 - lon1) * (Math.PI / 180)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

// Optimize route order using nearest neighbor algorithm
export const optimizeRouteOrder = (addresses) => {
  if (!addresses || addresses.length <= 2) {
    return addresses
  }

  const pickup = addresses[0] // Keep pickup as first
  const deliveryAddresses = addresses.slice(1) // All delivery addresses

  // Filter out addresses without coordinates
  const validDeliveryAddresses = deliveryAddresses.filter(
    (addr) => addr.lat !== undefined && addr.lng !== undefined
  )

  if (validDeliveryAddresses.length <= 1) {
    return addresses
  }

  const optimized = [pickup]
  const remaining = [...validDeliveryAddresses]

  let currentLocation = pickup

  // Use nearest neighbor algorithm to find optimal order
  while (remaining.length > 0) {
    let nearestIndex = 0
    let nearestDistance = calculateDistance(
      currentLocation.lat,
      currentLocation.lng,
      remaining[0].lat,
      remaining[0].lng
    )

    // Find the nearest unvisited address
    for (let i = 1; i < remaining.length; i++) {
      const distance = calculateDistance(
        currentLocation.lat,
        currentLocation.lng,
        remaining[i].lat,
        remaining[i].lng
      )
      if (distance < nearestDistance) {
        nearestDistance = distance
        nearestIndex = i
      }
    }

    // Add the nearest address to the optimized route
    const nearestAddress = remaining.splice(nearestIndex, 1)[0]
    optimized.push(nearestAddress)
    currentLocation = nearestAddress
  }

  return optimized
}
